import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TableComponent } from '../../../core/table/table.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { BillsResponse } from '../../../../models/api/view/claim/financials/bills/bills-response';
import { BillItem } from '../../../../models/api/view/claim/financials/bills/bill-item';
import { ExportService } from '../../../../services/export/export.service';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { InvoiceExportMapper } from '../../../../utilities/invoice-export-mapper';
import { DatePipe } from '@angular/common';
import { ValidatorsEx } from '../../../../validators/validators-ex';
import { InvoiceBillService } from './invoice-bill.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { EorReprintModalComponent } from './eor-reprint-modal.component';
import { BillDataTransformer } from '../../../../utilities/bill-data-transformer';
import { BillSearchParamsBuilder } from '../../../../utilities/bill-search-params-builder';
import { BillDropdownOptionsBuilder } from '../../../../utilities/bill-dropdown-options-builder';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-invoice-list',
  imports: [
    RouterModule,
    PageHeaderComponent,
    SelectComponent,
    InputComponent,
    RangeComponent,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    EorReprintModalComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    ToastComponent,
  ],
  providers: [InvoiceBillService],
  template: `
    <app-page-header label="Bill/Invoice List" labelCase="none" />
    <form [formGroup]="fg">
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0">
        <app-select
          [options]="payeeOptions"
          optionLabel="label"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.payee"
          label="Payee name"
          [multiple]="false"
        />
        <app-select
          [options]="vendorOptions"
          optionLabel="label"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.vendor"
          label="Vendor name"
          [multiple]="false"
        />
        <app-select
          [options]="treatingOptions"
          optionLabel="label"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [control]="fg.controls.treating"
          label="Treating provider"
          [multiple]="false"
          [virtualScroll]="false"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0"
      >
        <app-input
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [control]="fg.controls.icn"
          label="Internal control number"
        />
        <app-select
          [options]="billStatusOptions"
          optionLabel="label"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [control]="fg.controls.billStatus"
          label="Bill status"
          [multiple]="false"
          [virtualScroll]="false"
        />
        <app-range
          label="Sedgwick received date"
          [control]="fg.controls.transactionDateStart"
          [toControl]="fg.controls.transactionDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
      </div>
      <div class="tw-pt-4"></div>
      <app-action-row>
        <app-button
          left1
          emphasis="Low"
          [disabled]="!canRequestEorReprint()"
          [loading]="eorLoading"
          (click)="onRequestEorReprint()"
          labelCase="none"
          label="Request EOR reprint"
        />
        @if (eorMaxCountMessage) {
          <app-toast
            left2
            type="Info"
            [dismiss]="false"
            class="tw-hidden md:tw-block [&_.toast-container]:tw-items-center"
            >{{ eorMaxCountMessage }}</app-toast
          >
        }

        <app-button
          right1
          type="submit"
          (click)="onSearch()"
          [loading]="loading"
          label="Search"
        />
        <app-button right2 emphasis="Low" (click)="onClear()" label="Clear" />
      </app-action-row>
    </form>
    <div class="tw-flex tw-flex-row tw-w-full">
      @if (loading) {
        <app-empty-state
          height="300px"
          icon="fa-light fa-file-invoice"
          label="Loading bills/invoices"
          message="Retrieving from bill repository"
          class="tw-w-full"
        >
          <div class="tw-py-4">
            <app-progressbar />
          </div>
        </app-empty-state>
      } @else if (error) {
        <app-empty-state
          height="300px"
          icon="fa-light fa-triangle-exclamation"
          label="An error has occurred"
          class="tw-w-full"
        />
      } @else {
        <app-table
          #tablePayment
          class="tw-w-full"
          [dataSource]="bills"
          [pageSize]="10"
          [scrollable]="false"
          [expandable]="true"
          [showExport]="true"
          (export)="onExport($event)"
          (selectedEvent)="onSelectionChange($event)"
          dataKey="row"
          recordLabel="Bill/Invoice"
          recordLabelCase="lower"
          [stripedRows]="true"
          [showPaginator]="true"
          [showSelection]="true"
          sortField="rcvdSedgwick"
          [sortOrder]="-1"
          [isRowSelectable]="isEorEligible"
        >
          <app-column name="icn" label="Internal control number">
            <ng-template let-data>
              @if (canAccessDetailLinks) {
                <a
                  class="tw-text-[var(--sed-link-color)] tw-underline tw-cursor-pointer"
                  tabindex="0"
                  (click)="navigateToInvoiceDetail(data)"
                  (keydown.enter)="navigateToInvoiceDetail(data)"
                  (keydown.space)="
                    navigateToInvoiceDetail(data); $event.preventDefault()
                  "
                  >{{ data.icn || data.Icn }}</a
                >
              } @else {
                <span class="tw-text-[var(--text-color)]">{{
                  data.icn || data.Icn
                }}</span>
              }
            </ng-template>
          </app-column>
          <app-column name="billStatus" label="Bill status" />
          <app-column
            name="rcvdSedgwick"
            label="Sedgwick received date"
            format="d"
            [sort]="true"
          />
          <app-column name="billedAmount" label="Billed amount" format="c" />
          <app-column
            name="recAmount"
            label="Recommended allowance"
            format="c"
          />
          <app-column name="payee" label="Payee name" />
          <ng-template #expand let-data>
            <div class="tw-flex">
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Treating provider"
                    name="treatingProvider"
                  />
                  <app-detail-item label="Vendor name" name="vendorName" />
                  <app-detail-item
                    label="Provider Paid"
                    name="providerPaid"
                    format="d"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item label="Bill category" name="billCategory" />
                  <app-detail-item
                    label="Service Begin date"
                    name="dosBegin"
                    format="d"
                  />
                  <app-detail-item
                    label="Service end date"
                    name="dosEnd"
                    format="d"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item label="DCN" name="dcn" labelCase="none" />
                  <app-detail-item
                    label="SIR DCN"
                    name="sirDcn"
                    labelCase="none"
                  />
                  <app-detail-item
                    label="JURIS received date"
                    name="rcvdJuris"
                    format="d"
                    labelCase="none"
                  />
                </app-detail-panel>
              </div>
            </div>
          </ng-template>
        </app-table>
      }
    </div>

    <!-- EOR Reprint Processing Modal -->
    <app-eor-reprint-modal
      [visible]="showEorReprintModal"
      [loading]="eorReprintModalLoading"
      [error]="eorReprintModalError"
      (close)="closeEorReprintModal()"
    />
  `,
  styles: ``,
})
export class InvoiceListComponent extends BaseComponent implements OnInit {
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);
  private invoiceBillService = inject(InvoiceBillService);
  private snackbar = inject(Snackbar);

  @ViewChild('tablePayment') tablePayment!: TableComponent;

  loading = true;
  error: any = null;
  disability = false;
  workersComp = false;
  billsResponse: BillsResponse | null = null;
  bills: any[] = [];
  initialBills: any[] = []; // Store initial bills for Clear functionality

  // Security: Check if user can access detail links (BillingFull vs BillingLimited)
  canAccessDetailLinks = false;

  // EOR functionality properties
  eorLoading = false;
  maxEorCount = 70;
  eorMaxCountMessage: string | null = null;

  // EOR reprint modal state
  showEorReprintModal = false;
  eorReprintModalError: string | null = null;
  eorReprintModalLoading = false;

  fg = new FormGroup({
    payee: new FormControl<string>(null),
    vendor: new FormControl<string>(null),
    billStatus: new FormControl<string>(null),
    treating: new FormControl<string>(null),
    icn: new FormControl<string>(null, [
      ValidatorsEx.alphaNumericHyphen(
        'Internal control number must contain only letters, numbers, and hyphens',
      ),
      Validators.maxLength(50),
    ]),
    transactionDateStart: new FormControl<Date>(null),
    transactionDateEnd: new FormControl<Date>(null),
  });

  payeeOptions: { label: string; key: string }[] = [];

  treatingOptions: { label: string; key: string }[] = [];

  billStatusOptions: { label: string; key: string }[] = [];

  vendorOptions: { label: string; key: string }[] = [];

  @HandleLoadingState({ useViewLoading: true })
  async ngOnInit() {
    const claim = this.view.currentClaim();
    if (!claim) {
      this.router.navigate(['/view']);
      return;
    }

    this.disability = claim.lineOfBusiness === 'DS';
    this.workersComp = claim.lineOfBusiness === 'WC';

    // Check if user has BillInvoiceDetailLinks token (BillingFull access)
    this.canAccessDetailLinks = this.session.authorize(
      'BillInvoiceDetailLinks',
    );

    // Get initial bills data
    const claimKey: ClaimKey = {
      recordId: claim.recordId,
      recordSource: claim.recordSource,
    };

    this.billsResponse = await this.api.bills.getInitialBills(claimKey);

    // Map API data to bills array with row indexes for expandable table
    const billsArray = BillDataTransformer.extractBillsArray(
      this.billsResponse,
    );

    if (billsArray && Array.isArray(billsArray)) {
      // Transform bills using utility
      this.bills = BillDataTransformer.transformBills(billsArray, {
        claimNumber: claim.claimNumber,
        fileNumber: claim.fileNumber,
        claimantFirstName: claim.claimantFirstName,
        claimantLastName: claim.claimantLastName,
        accountName: claim.accountName,
      });

      this.populateDropdownOptions();

      // Store initial bills for Clear functionality
      this.initialBills = [...this.bills];
    }
  }

  /**
   * Populate dropdown options with unique values from bills data
   */
  private populateDropdownOptions() {
    const options = BillDropdownOptionsBuilder.buildOptions(this.bills);
    this.payeeOptions = options.payeeOptions;
    this.vendorOptions = options.vendorOptions;
    this.treatingOptions = options.treatingOptions;
    this.billStatusOptions = options.billStatusOptions;
  }

  @HandleLoadingState()
  async onSearch() {
    const claim = this.view.currentClaim();
    const claimKey: ClaimKey = {
      recordId: claim.recordId,
      recordSource: claim.recordSource,
    };

    // Build search parameters using utility
    const searchParams = BillSearchParamsBuilder.build({
      payee: this.fg.controls.payee.value,
      vendor: this.fg.controls.vendor.value,
      treating: this.fg.controls.treating.value,
      billStatus: this.fg.controls.billStatus.value,
      icn: this.fg.controls.icn.value,
      transactionDateStart: this.fg.controls.transactionDateStart.value,
      transactionDateEnd: this.fg.controls.transactionDateEnd.value,
    });

    if (!searchParams) {
      return;
    }

    // Call the search API
    this.billsResponse = await this.api.bills.searchBills(
      claimKey,
      searchParams,
    );

    // Map the response to bills array
    const billsArray = BillDataTransformer.extractBillsArray(
      this.billsResponse,
    );

    if (billsArray && Array.isArray(billsArray)) {
      // Transform bills using utility
      this.bills = BillDataTransformer.transformBills(billsArray, {
        claimNumber: claim.claimNumber,
        fileNumber: claim.fileNumber,
        claimantFirstName: claim.claimantFirstName,
        claimantLastName: claim.claimantLastName,
        accountName: claim.accountName,
      });
    } else {
      this.bills = [];
    }
  }

  onClear() {
    // Reset all form controls to their initial state
    this.fg.controls.payee.setValue(null);
    this.fg.controls.vendor.setValue(null);
    this.fg.controls.treating.setValue(null);
    this.fg.controls.billStatus.setValue(null);
    this.fg.controls.icn.setValue(null);
    this.fg.controls.transactionDateStart.setValue(null);
    this.fg.controls.transactionDateEnd.setValue(null);

    // Restore initial bills data (create new array reference to trigger change detection)
    this.bills = this.initialBills.slice();

    // Reset table state (pagination, filters, sorting, etc.)
    if (this.tablePayment?.table) {
      this.tablePayment.table.reset();
    }
  }

  /**
   * Check if a bill is eligible for EOR reprint
   * Pending records are excluded from EOR reprint functionality
   */
  isEorEligible = (bill: any): boolean => {
    if (!bill || !bill.billStatus) {
      return false;
    }

    // Exclude pending records - they are not eligible for EOR reprint
    const status = (bill.billStatus || '').toUpperCase();
    return status !== 'PENDING';
  };

  navigateToInvoiceDetail(billData: any) {
    // Extract ICN from bill data (handle both lowercase and uppercase property names)
    const icn = billData?.icn || billData?.Icn;

    if (!icn) {
      return;
    }

    // Navigate to invoice detail page with ICN in URL parameter
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/'));

    this.router.navigate([`${baseUrl}/invoice-bill/${icn}`], {
      queryParams: {
        backLabel: 'Bill/invoice',
        backRouterLink: '../invoice-list',
      },
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat) {
    // Sort bills by rcvdJuris descending (newest first) using utility
    const rows = BillDataTransformer.sortByRcvdSedgwick([...this.bills]);

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `BillList_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = InvoiceExportMapper.toCsvExport(
        rows,
        'Bill/Invoice Export',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = InvoiceExportMapper.toExcelExport(
        rows,
        'Bill/Invoice Export',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }

  /**
   * Get currently selected bills from the table
   */
  getSelectedBills(): BillItem[] {
    return this.tablePayment?.getSelection() || [];
  }

  /**
   * Check if EOR reprint button should be enabled
   */
  canRequestEorReprint(): boolean {
    const selectedBills = this.getSelectedBills();
    return (
      selectedBills.length > 0 &&
      selectedBills.length <= this.maxEorCount &&
      !this.eorLoading
    );
  }

  /**
   * Handle selection change event from the table
   * Shows toast when max EOR count is exceeded
   */
  onSelectionChange(selectedBills: any[]): void {
    if (selectedBills.length > this.maxEorCount) {
      const message = `The maximum EORs that can be processed at once is ${this.maxEorCount}`;
      if (this.breakpoint.isMobile()) {
        this.snackbar.show(message, 'Info');
      } else {
        this.eorMaxCountMessage = message;
      }
    } else {
      // Clear the message when selection is back within limits
      this.eorMaxCountMessage = null;
    }
  }

  /**
   * Handle EOR reprint request
   */
  @HandleLoadingState({ errorProperty: 'actionError' })
  async onRequestEorReprint(): Promise<void> {
    const selectedBills = this.getSelectedBills();

    // Clear any previous max count message
    this.eorMaxCountMessage = null;

    // Validate selection using service
    const validation = this.invoiceBillService.validateEorEligibility(
      selectedBills,
      this.maxEorCount,
    );
    if (!validation.valid) {
      this.snackbar.show(validation.message!, 'Error');
      return;
    }

    // Show the processing modal
    this.showEorReprintModal = true;
    this.eorReprintModalLoading = true;
    this.eorReprintModalError = null;
    this.eorLoading = true;

    const claim = this.view.currentClaim();
    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };

    // Process EOR reprint using service
    const result = await this.invoiceBillService.processEorReprint(
      claimKey,
      selectedBills,
    );

    // Handle result
    if (result.success) {
      // Show success or partial success message
      if (result.partialSuccess && result.failureCount! > 0) {
        let message = result.error;
        if (!message) {
          message = `Of the ${result.totalCount} selected EORs, ${result.failureCount} EOR(s) were not available.`;
        }
        this.snackbar.show(`EOR Reprint: ${message}`, 'Info');
      } else {
        this.snackbar.show(
          `Successfully downloaded ${result.successCount} EOR(s).`,
          'Success',
        );
      }

      // Close modal on success
      this.closeEorReprintModal();
    } else {
      // Show error in modal
      this.eorReprintModalLoading = false;
      this.eorReprintModalError =
        result.error || 'An error occurred while requesting the EOR.';
    }

    this.eorLoading = false;
  }

  /**
   * Close the EOR reprint modal
   */
  closeEorReprintModal(): void {
    this.showEorReprintModal = false;
    this.eorReprintModalError = null;
    this.eorReprintModalLoading = false;
  }
}
